import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationNgRxActions } from '@geekbears/gb-ngrx-auth-basic';
import { getFailureMessageRxOp } from '@geekbears/gb-ngrx-stack-base';
import { ModalService, NotificationsService } from '@geekbears/ngx-gb-ui-kit';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { isNil } from 'lodash';
import {
  delay,
  filter,
  map,
  Observable,
  Subject,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';

import { AccountSetupCompleteModal } from '@modules/shared/presentation';
import { APP_ROUTES } from '@typings/app-routes.constant';
import {
  getUserDetailsFailureAction,
  updateCompanyManagerFailureAction,
  updateCompanyManagerSuccessAction,
  UsersActions,
} from '@modules/company-manager/users/application';
import { ProfileActions } from '@modules/regular-user/profile/application';
import { appAuthenticatedUserSelector } from '@core/application';
import { AppUser } from '@modules/company-manager/users/domain';

@Component({
  selector: 'app-personal-information',
  templateUrl: './personal-information.page.html',
  styleUrls: ['./personal-information.page.scss'],
})
export class PersonalInformationComponent implements OnInit, OnDestroy {
  destroy$: Subject<void> = new Subject();

  form!: FormGroup;
  authUser$: Observable<AppUser>;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private notificationsService: NotificationsService,
    private modalService: ModalService,
    private store$: Store,
    private actions$: Actions
  ) {
    this.form = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      jobTitle: ['', [Validators.required]],
      department: ['', [Validators.required]],
      role: ['', [Validators.required]],
    });
    this.authUser$ = this.store$.select(appAuthenticatedUserSelector).pipe(
      filter((user) => {
        return !isNil(user);
      }),
      map((user) => user!)
    );
    this.getUserDetails();
  }

  ngOnInit(): void {
    /* Update fields with most resent data */
    this.authUser$
      .pipe(
        map((user) => {
          this.form.get('firstName')?.setValue(user!.firstName);
          this.form.get('lastName')?.setValue(user!.lastName);
          this.form.get('jobTitle')?.setValue(user!.jobTitle);
          this.form.get('department')?.setValue(user!.department);
          this.form.get('role')?.setValue(user!.roles);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.onFailureActions();
    this.onUpdateCompanyManagerSuccess();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onUpdateCompanyManagerSuccess() {
    this.actions$
      .pipe(
        ofType(updateCompanyManagerSuccessAction),
        delay(100),
        withLatestFrom(this.authUser$),
        tap(([_, user]) => {
          if (user.requiresPasswordChange) {
            this.router.navigateByUrl(APP_ROUTES.dashboard.fullPath);
          } else {
            this.openModalAccountCompleted();
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  back() {
    // localStorage.clear();
    localStorage.removeItem('s_admin');
    localStorage.removeItem('confirmAccountToken');
    localStorage.removeItem('confirmAccountEmail');
    this.store$.dispatch(AuthenticationNgRxActions.logout());
    setTimeout(() => {
      this.router.navigateByUrl(APP_ROUTES.authentication['sign-in'].fullPath);
    }, 300);
  }

  getUserDetails() {
    this.store$.dispatch(AuthenticationNgRxActions.getAuthenticatedUserData());
  }

  setPersonalInformation() {
    if (this.form.valid) {
      this.store$.dispatch(
        UsersActions.updateCompanyManager({
          userData: this.form.value,
        })
      );
    }
  }

  successModal(message: string) {
    this.notificationsService.success(message, {
      duration: 4000,
      icon: 'check-circle',
    });
  }

  errorModal(message: string) {
    this.notificationsService.error(message, {
      duration: 4000,
      icon: 'alert-outline',
    });
  }

  openModalAccountCompleted() {
    this.modalService.open(AccountSetupCompleteModal);
  }

  onFailureActions() {
    this.actions$
      .pipe(
        ofType(getUserDetailsFailureAction, updateCompanyManagerFailureAction),
        map((action) => {
          return action.failure;
        }),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          if (errorMessage) {
            this.notificationsService.error(errorMessage, {
              duration: 5000,
              icon: 'alert-outline',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }
}
